'use client';
// Plan d'actions CAPA (design isActions — ISO 15189 §8.7) : la clôture exige
// une vérification d'efficacité tracée, signée par le RQ.
import React, { useEffect, useMemo, useState } from 'react';
import { useOngletAdresse } from '@/lib/useAdresse';
import { LigneLien } from '@/shared/Tableau';
import { cheminDe } from '@/lib/adresse';
import { useGed, userName } from '@/lib/store';
import { ACTION_TYPES, ACTION_ORIGINS, ACTION_STATES, vocabMeta, fmtDate, fmtDateTime, joursRestants, MOTIF_DEROGATION_MIN } from '@/lib/meta';
import {
  TitrePage, I, card, inputStyle,
  Skeleton, SkeletonTiles, EmptyState, FilterBar, LateBadge, FieldError, RequiredHint, Help, ConfirmButton, useBusy, RefISO, Selecteur, BoutonCreer, MentionTri } from '@/components/ui';
import { EnteteEcran } from '@/components/EnteteEcran';
import { Liens } from '@/components/Liens';
import { useFormulaire } from '@/components/Formulaire';
import { LienFiche } from '@/components/LienFiche';
import { BoutonJournal } from '@/components/BoutonJournal';
import { JournalFiche } from '@/components/JournalFiche';
import { OngletsDossier, PanneauDossier, BarreAction } from '@/components/Dossier';
// Le panneau « dire ce que le geste emporte », partagé avec les NC et les retraits.
import { SuppressionRaisonnee } from '@/components/SuppressionRaisonnee';
import type { GedAction } from '@/lib/types';
import { useTri, BoutonsTri } from '@/shared/Tri';
import { Norme } from '@/components/Norme';
// L'état d'avancement, comme au traitement des NC (RQ, 07/10/2026).
import { FriseAction, ConsigneEtapeAction } from '@/components/ParcoursAction';
import { etapeCouranteAction, prochainGeste, resteAFaireAction, type EtapeAction } from '@/lib/parcours-action';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };
/** Une action non soldée : ni réalisée, ni vérifiée. Source unique — le
 *  tableau de bord s'en sert aussi, pour ne pas diverger du registre. */
export const actionOuverte = (a: GedAction) => !['realisee', 'verifiee', 'annulee'].includes(a.statut);
/** « Mes » actions : celles dont je porte la responsabilité. */
export const actionMienne = (a: GedAction, cle: string) => a.responsable === cle;
export const isLate = (a: GedAction) => a.echeance && actionOuverte(a) && new Date(a.echeance) < new Date();

const AIDE_CAPA = 'CAPA : Corrective And Preventive Action — le dispositif qui traite la cause d’un problème constaté (corrective) ou d’un risque identifié (préventive), et non seulement ses effets.';
/** Le message de confirmation, par état visé — le libellé du geste vit dans
 *  `lib/parcours-action.ts`, le toast reste ici : il parle d'écriture réussie. */
const TOAST_GESTE: Record<string, string> = {
  encours: 'Action démarrée.',
  realisee: 'Action marquée réalisée — en attente de vérification d’efficacité.',
};
const AIDE_VERIF = 'Vérification d’efficacité : contrôle, après coup, que l’action a bien supprimé la cause — par exemple l’absence de récidive sur une période donnée. Sans elle, l’action ne peut pas être clôturée (ISO 15189 §8.7).';

/** Le formulaire reste monté quand on le masque : un clic exploratoire dans la
 *  liste ne doit pas effacer une déclaration à moitié saisie. */
function NewForm({ visible, onDone, onDirty }: { visible: boolean; onDone: () => void; onDirty: (d: boolean) => void }) {
  const { smq, settings, ncs, recls } = useGed();
  const vide = { titre: '', actType: 'corrective', origine: 'autre', origineRef: '', responsable: '', echeance: '', description: '' };
  const [f, setF] = useState(vide);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const gedUsers = settings?.gedUsers || [];

  const modifie = JSON.stringify(f) !== JSON.stringify(vide);
  useEffect(() => { onDirty(modifie); }, [modifie, onDirty]);

  // L'origine typée fournit la liste des références possibles : la saisie libre
  // laissait passer les fautes de frappe, qui rendaient les deux objets orphelins.
  const refsOrigine = f.origine === 'nc'
    ? ncs.map((n) => ({ v: n.ref, l: `${n.ref} — ${n.titre}` }))
    : f.origine === 'reclamation'
      ? recls.map((r) => ({ v: r.ref, l: `${r.ref} — ${r.objet}` }))
      : null;

  /* Le contrôle passe au socle partagé : l'intitulé manquant était signalé en
     rouge sous le champ, ce qui ne se voit pas quand on a cliqué depuis le bas
     du formulaire. Le bandeau le nomme et y emmène. */
  const declaration = React.useMemo(() => [
    { id: 'act-titre', label: 'Intitulé', valeur: f.titre, requis: true },
  ], [f.titre]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const creer = () => run('creer', async () => {
    if (!verifier()) return;
    setErreur('');
    const ok = await smq('ged/actions', f, 'Action créée au plan.');
    if (ok) { setF(vide); onDone(); }
  });

  return (
    <div ref={refFormulaire} style={{ ...card, padding: 20, display: visible ? 'block' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 14px' }}>
        <h2 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Nouvelle action</h2>
        <RequiredHint />
      </div>
      <Bandeau />
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl} htmlFor="act-titre">Intitulé <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="act-titre" value={f.titre} onChange={(e) => { setF({ ...f, titre: e.target.value }); if (erreur) setErreur(''); }}
            aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
          <FieldError>{erreur}</FieldError>
        </div>
        <div><label style={lbl}>Type</label>
          <select value={f.actType} onChange={(e) => setF({ ...f, actType: e.target.value })} style={inputStyle}>
            {ACTION_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select></div>
        <div><label style={lbl}>Origine</label>
          <select value={f.origine} onChange={(e) => setF({ ...f, origine: e.target.value, origineRef: '' })} style={inputStyle}>
            {ACTION_ORIGINS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select></div>
        {/* `htmlFor`/`id` : le `<label>` était posé À CÔTÉ du champ sans lui être
            rattaché. Il se lit à l'œil, mais cliquer dessus ne donne pas le
            focus et un lecteur d'écran ne l'annonce pas avec le champ. */}
        <div><label htmlFor="action-origine-ref" style={lbl}>Référence d’origine (NC, audit…)</label>
          {refsOrigine ? (
            <Selecteur id="action-origine-ref" value={f.origineRef} onChange={(v) => setF({ ...f, origineRef: v })} style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }}>
              <option value="">—</option>
              {refsOrigine.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </Selecteur>
          ) : (
            <input id="action-origine-ref" value={f.origineRef} onChange={(e) => setF({ ...f, origineRef: e.target.value })} placeholder="Ex. BIO24-AUD-2026-002" style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} />
          )}
        </div>
        <div><label style={lbl}>Responsable</label>
          <Selecteur value={f.responsable} onChange={(v) => setF({ ...f, responsable: v })} style={inputStyle}>
            <option value="">—</option>
            {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
          </Selecteur></div>
        <div><label style={lbl}>Échéance</label>
          <input type="date" value={f.echeance} onChange={(e) => setF({ ...f, echeance: e.target.value })} style={inputStyle} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>Sans échéance, l’action ne pourra jamais être signalée en retard.</div>
        </div>
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl}>Description</label><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} /></div>
      </div>
      <PiedActions taille="sm" style={{ marginTop: 14 }} annuler={{ onClick: () => { setF(vide); setErreur(''); onDone(); } }} principale={{ libelle: 'Créer l’action', libelleChargement: 'Création…', chargement: estOccupe('creer'), disabled: estOccupe(), onClick: () => void creer() }} />
    </div>
  );
}

/**
 * LE SUIVI D'UNE ACTION — ce qu'on fait, pour quand, et par qui.
 *
 * Deux manques, signalés ensemble par la RQ le 07/10/2026 :
 *
 *   · la fiche n'offrait aucun formulaire d'édition du TEXTE : une action née
 *     d'une NC arrivait au plan avec la phrase recopiée de la NC, et « dire ce
 *     qu'on fait, le préciser ou le modifier » obligeait à repasser par la
 *     fenêtre de rédaction, qui ne s'ouvre qu'à la naissance de l'action ;
 *   · une action née sans échéance — ce que fait toute action créée depuis une
 *     NC — le restait définitivement, donc invisible aux retards.
 *
 * Les trois champs tiennent dans le MÊME formulaire : ils répondent à la même
 * question (« où en est cette action ? ») et la route les écrit d'un seul coup,
 * en un seul événement de journal. Deux boutons d'édition côte à côte auraient
 * demandé de savoir lequel ouvre quoi.
 *
 * La route est celle du plan d'actions (`/plan`) : elle refuse d'elle-même une
 * action clôturée et journalise chaque champ modifié.
 */
function SuiviAction({ a, peutModifier }: { a: GedAction; peutModifier: boolean }) {
  const { smq, settings } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [description, setDescription] = useState(a.description || '');
  const [echeance, setEcheance] = useState(a.echeance || '');
  const [responsable, setResponsable] = useState(a.responsable || '');
  /* CE QUE LE FORMULAIRE A LU, FIGÉ À L'OUVERTURE (14/10/2026). Envoyé sous
     `base` : le moteur n'écrit que les champs changés ici. Sans elle, ouvrir
     le suivi pour corriger l'échéance renvoyait aussi la description lue à
     l'ouverture — et effaçait celle qu'un collègue venait d'enregistrer. */
  const [base, setBase] = useState<Record<string, string>>({
    description: a.description || '', echeance: a.echeance || '', responsable: a.responsable || '',
  });
  const ouvrir = () => {
    const lu = { description: a.description || '', echeance: a.echeance || '', responsable: a.responsable || '' };
    setDescription(lu.description); setEcheance(lu.echeance); setResponsable(lu.responsable);
    setBase(lu);
    setOuvert(true);
  };
  const { run, estOccupe } = useBusy();
  const gedUsers = settings?.gedUsers || [];
  const late = isLate(a);
  // Resynchronisation PENDANT LE RENDU plutôt que dans un effet : quand l'action
  // affichée change, les champs doivent repartir de ses valeurs. Un effet qui
  // appelle setState déclenche un rendu en cascade (React 19).
  const [vue, setVue] = useState(a.documentId);
  if (vue !== a.documentId) {
    setVue(a.documentId);
    setDescription(a.description || '');
    setEcheance(a.echeance || '');
    setResponsable(a.responsable || '');
  }

  if (ouvert) {
    return (
      <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 10 }}>
        <div>
          <label style={lbl} htmlFor={`act-suivi-${a.documentId}`}>Ce qu’il faut faire, et ce qui est fait</label>
          <textarea id={`act-suivi-${a.documentId}`} value={description} rows={4}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ex. procédure PR-PRE-003 revue avec l’équipe du prélèvement le 12/10 ; reste la formation des deux nouveaux."
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
            Ce texte est le suivi lu en audit : complétez-le au fil de l’eau. La modification part au journal signé.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 160px' }}>
            <label style={lbl} htmlFor={`act-ech-${a.documentId}`}>Échéance</label>
            <input id={`act-ech-${a.documentId}`} type="date" value={echeance} onChange={(e) => setEcheance(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <label style={lbl} htmlFor={`act-resp-${a.documentId}`}>Responsable</label>
            <Selecteur id={`act-resp-${a.documentId}`} value={responsable} onChange={setResponsable} style={inputStyle}>
              <option value="">—</option>
              {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
            </Selecteur>
          </div>
        </div>
        <PiedActions
          taille="sm"
          annuler={{ onClick: () => {
            setDescription(a.description || '');
            setEcheance(a.echeance || '');
            setResponsable(a.responsable || '');
            setOuvert(false);
          } }}
          principale={{
            libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('plan'), disabled: estOccupe(),
            onClick: () => void run('plan', async () => {
              const ok = await smq(`ged/actions/${a.documentId}/plan`, { description, echeance, responsable, base },
                'Suivi enregistré — événement tracé.');
              if (ok) setOuvert(false);
            }),
          }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ ...lbl, marginBottom: 0 }}>Suivi</span>
        <span style={{ flex: 1 }} />
        {peutModifier && (
          <Bouton taille="sm" onClick={ouvrir} style={{ width: 'fit-content' }}>
            <I n="pen" s={13} />Modifier le suivi
          </Bouton>
        )}
      </div>
      {a.description
        ? <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{a.description}</p>
        : <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Aucun suivi écrit — rien ne dit encore ce qu’il faut faire.
          </p>}
      <div style={{ display: 'flex', gap: 18, fontSize: 12.5, color: 'var(--text-body)', flexWrap: 'wrap' }}>
        <span>Responsable : <strong>{a.responsable ? userName(settings, a.responsable) : '—'}</strong></span>
        <span>Échéance : <strong style={{ color: late ? 'var(--red-600)' : undefined }}>{a.echeance ? fmtDate(a.echeance) : 'aucune'}</strong></span>
        {a.dateRealisation && <span>Réalisée le : <strong>{fmtDate(a.dateRealisation)}</strong></span>}
      </div>
    </div>
  );
}

type OngletAction = 'suivi' | 'efficacite' | 'liens';
/* Les valeurs ADMISES dans l'adresse. Elle existe pour refuser
   `?onglet=nimportequoi` : sans elle, une adresse tronquée par un logiciel de
   messagerie ouvrirait un onglet vide au lieu du premier. */
const ONGLETS_ONGLETACTION: readonly OngletAction[] = ['suivi', 'efficacite', 'liens'];

/**
 * Annuler une action inscrite au plan par erreur.
 *
 * Même modèle que la non-conformité : on dit ce que le geste emporte avant de
 * l'emporter. La conséquence propre à l'action est nommée en toutes lettres —
 * elle cesse de bloquer la clôture de son origine. C'est le point sensible du
 * geste, il ne doit pas s'apprendre après coup.
 */
function AnnulerAction({ a }: { a: GedAction }) {
  const { smq } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');

  if (!ouvert) {
    return (
      <Bouton taille="sm" variante="discret" onClick={() => setOuvert(true)}
        title="L’action n’a pas lieu d’être : la retirer du plan, en le motivant"
        style={{ marginInlineStart: 'auto' }}>
        Inscrite par erreur…
      </Bouton>
    );
  }

  return (
    <div style={{ flex: '1 1 100%' }}>
      <SuppressionRaisonnee
        titre="Annuler cette action — inscrite par erreur"
        objet={<><strong>{a.ref}</strong> — {a.titre}</>}
        nature="retrait"
        consequences={[
          ...(a.origineRef ? [{
            cle: 'origine',
            texte: `${a.origineRef} ne sera plus bloquée par cette action.`,
            gravite: 'attention' as const,
            detail: 'Elle ne comptera pas non plus parmi les actions menées à la clôture : le journal ne pourra pas s’en prévaloir.',
          }] : []),
          {
            cle: 'compteurs',
            texte: 'L’action sort du plan et de ses compteurs (ouvertes, en retard).',
            gravite: 'attention' as const,
          },
          {
            cle: 'trace',
            texte: 'La ligne, sa référence et son journal restent lisibles.',
            gravite: 'info' as const,
            detail: 'Rien n’est effacé — le motif ci-dessous s’inscrit au journal signé.',
          },
        ]}
        note={(
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={lbl} htmlFor={`annul-${a.ref}`}>
              Motif de l’annulation <span style={{ color: 'var(--red-600)' }}>*</span>
            </label>
            <textarea id={`annul-${a.ref}`} value={motif} rows={2}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Ex. doublon de BIO24-AC-2026-018, créée deux fois depuis la même non-conformité."
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
            <span>{motif.trim().length}/{MOTIF_DEROGATION_MIN} caractères minimum — relu en audit.</span>
          </div>
        )}
        libelleAction="Annuler l’action"
        onConfirmer={async () => {
          if (motif.trim().length < MOTIF_DEROGATION_MIN) {
            return `Motif trop court : ${MOTIF_DEROGATION_MIN} caractères au minimum.`;
          }
          const ok = await smq(`ged/actions/${a.documentId}/annuler`, { motif: motif.trim() },
            'Action annulée — motif inscrit au journal.', `Annulation de ${a.ref}`);
          return ok ? '' : 'L’annulation n’a pas abouti.';
        }}
        onAnnuler={() => { setOuvert(false); setMotif(''); }} />
    </div>
  );
}

/**
 * Le DOSSIER d'une action corrective — exporté sous le nom `ActionDossier`.
 *
 * Exporté parce qu'il est désormais affiché à deux endroits : ici, dans le plan
 * d'actions, et EN PANNEAU par-dessus la fiche de non-conformité (N2-4). Le
 * recopier aurait donné deux fiches action qui divergent, dont l'une n'aurait
 * jamais reçu les corrections de l'autre — c'est exactement la duplication qui
 * a rendu `Ressources.tsx` ingérable.
 *
 * `enPanneau` ne change rien au contenu : il retire seulement le cadre de
 * carte, puisque le panneau en fournit un (sans quoi : carte dans une carte,
 * double bordure, double ombre).
 */
export function ActionDossier({ a, enPanneau = false }: { a: GedAction; enPanneau?: boolean }) {
  return <Detail a={a} enPanneau={enPanneau} />;
}

function Detail({ a, enPanneau = false }: { a: GedAction; enPanneau?: boolean }) {
  const { smq, session, events, loadJournal, ncs, recls, go, ouvrirPanneau, liens } = useGed();
  const [verif, setVerif] = useState('');
  /* L'onglet vit dans l'ADRESSE (relevé P0.8) : il se partage par lien, F5 y
     revient, et « Précédent » ramène à l'onglet précédent au lieu de quitter
     l'écran. */
  const [onglet, setOnglet] = useOngletAdresse<OngletAction>('suivi', ONGLETS_ONGLETACTION);
  const { run, estOccupe } = useBusy();
  useEffect(() => { void loadJournal(a.ref); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [a.documentId, a.statut]);
  const isRQ = session.roles.includes('rq');
  const canManage = isRQ || a.responsable === session.key;
  const journal = events[a.ref] || [];
  const late = isLate(a);
  const nbLiens = liens.filter((l) => (l.sourceType === 'action' && l.sourceRef === a.ref) || (l.cibleType === 'action' && l.cibleRef === a.ref)).length;

  // Le dossier d'origine est l'objet LIÉ à l'action qu'on lit : il s'ouvre
  // PAR-DESSUS, pour ne pas perdre la fiche action en cours d'instruction.
  const gotoOrigin = () => {
    if (a.origine === 'nc') {
      const nc = ncs.find((n) => n.ref === a.origineRef);
      if (nc) ouvrirPanneau({ type: 'nc', valeur: nc.documentId, origine: a.ref });
    } else if (a.origine === 'reclamation') {
      const r = recls.find((x) => x.ref === a.origineRef);
      if (r) ouvrirPanneau({ type: 'recl', valeur: r.documentId, origine: a.ref });
    } else if (a.origine === 'eeq') {
      // Le registre EEQ n'a pas de fiche : on remonte au registre, où la ligne
      // porte déjà la référence de l'action.
      go('eeq');
    }
  };
  const originLinkable = (a.origine === 'nc' && ncs.some((n) => n.ref === a.origineRef))
    || (a.origine === 'reclamation' && recls.some((r) => r.ref === a.origineRef))
    || a.origine === 'eeq';
  // Une référence saisie mais introuvable est un lien rompu silencieux : le dire.
  const originRompu = !!a.origineRef && ['nc', 'reclamation'].includes(a.origine) && !originLinkable;

  const transition = (statut: string, okToast: string) =>
    run(statut, () => smq(`ged/actions/${a.documentId}/status`, { statut }, okToast));

  /* Les dates de la frise viennent du JOURNAL SIGNÉ — seule source qui les
     porte réellement (`dateCreation` et `dateRealisation` sont les deux seules
     que la fiche stocke). Le DERNIER franchissement, pas le premier : un même
     type d'événement peut revenir. */
  const dateEv = (...types: string[]) => {
    const l = journal.filter((e) => types.includes(e.evType));
    return l.length ? l[l.length - 1].ts : undefined;
  };
  const datesEtapes: Partial<Record<EtapeAction, string | undefined | null>> = {
    engagement: dateEv('ActionCreee') || a.dateCreation,
    execution: dateEv('ActionDemarree'),
    realisation: dateEv('ActionRealisee') || a.dateRealisation,
    efficacite: dateEv('ActionVerifiee'),
  };
  const etapeNow = etapeCouranteAction(a.statut);
  const reste = resteAFaireAction(etapeNow, a);
  // Le geste suivant, et ce qu'il AFFIRME — la phrase manquait au pied du dossier.
  const geste = prochainGeste(a.statut);
  const gestePourMoi = !!geste && (!geste.rqSeul || isRQ);

  return (
    <div style={enPanneau
      ? { padding: '4px 2px', display: 'grid', gap: 12 }
      : { ...card, padding: 20, display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.ref}</span>
        <LienFiche vue="actions" reference={a.ref} quoi="cette action" />
        <BoutonJournal reference={a.ref} quoi="de cette action" evenements={journal} />
        <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: vocabMeta(ACTION_STATES, a.statut).bg, color: vocabMeta(ACTION_STATES, a.statut).fg, fontSize: 11, fontWeight: 700 }}>{vocabMeta(ACTION_STATES, a.statut).label}</span>
        {late && <LateBadge jours={joursRestants(a.echeance)} />}
      </div>
      <h2 style={{ margin: 0, fontSize: 16.5, fontWeight: 700, color: 'var(--text-strong)' }}>{a.titre}</h2>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        {ACTION_TYPES.find((t) => t.key === a.actType)?.label} · origine {ACTION_ORIGINS.find((o) => o.key === a.origine)?.label}
        {/* En panneau, le dossier d'origine est JUSTE DESSOUS : « remonter à
            l'origine » n'y mènerait pas, il quitterait la superposition pour
            rouvrir ailleurs ce qu'on a déjà sous les yeux. On affiche donc la
            référence, sans promettre un voyage. */}
        {a.origineRef && <>
          {' '}· {originLinkable && !enPanneau
            ? <button onClick={gotoOrigin} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.origineRef} — remonter à l’origine →</button>
            : <span style={{ fontFamily: 'var(--font-mono)' }}>{a.origineRef}</span>}
        </>}
      </div>
      {originRompu && (
        <div role="alert" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--red-600)', background: 'var(--red-50)', borderRadius: 'var(--radius-md)', padding: '8px 12px' }}>
          La référence « {a.origineRef} » ne correspond à aucun enregistrement : le lien vers l’origine est rompu.
        </div>
      )}
      {/* L'ÉTAT D'AVANCEMENT, demandé « comme dans le traitement des NC »
          (RQ, 07/10/2026) : la pastille d'état disait OÙ l'on est, jamais dans
          quelle séquence. Les étapes ne sont pas les onglets — chacune mène à
          l'onglet où elle se joue, voir `lib/parcours-action.ts`. */}
      <FriseAction statut={a.statut} dates={datesEtapes} onAller={setOnglet} />
      <ConsigneEtapeAction statut={a.statut} reste={reste} />

      <OngletsDossier id="act" actif={onglet} onChange={setOnglet} items={[
        { k: 'suivi', label: 'Suivi' },
        { k: 'efficacite', label: 'Efficacité' },
        { k: 'liens', label: 'Liens', n: nbLiens },
      ]} />

      <PanneauDossier id="act" actif={onglet}>

      {onglet === 'suivi' && (<>
      {/* Le texte, l'échéance et le responsable s'éditent ICI : « je voudrais
          pouvoir modifier le texte de suivi pour dire ce qu'on fait, le
          préciser ou le modifier » (RQ, 07/10/2026). */}
      <SuiviAction a={a} peutModifier={canManage && a.statut !== 'verifiee' && a.statut !== 'annulee'} />
      {!a.echeance && a.statut !== 'verifiee' && a.statut !== 'annulee' && (
        <div style={{ fontSize: 12.5, color: 'var(--amber-700)', background: 'var(--amber-50)', borderRadius: 'var(--radius-md)', padding: '8px 12px' }}>
          Cette action n’a pas d’échéance : elle ne sera jamais comptée parmi les actions en retard.
        </div>
      )}
      </>)}

      {/* La vérification d'efficacité est ce qui distingue une action « faite »
          d'une action qui a résolu la cause (§8.7.2) : elle a son onglet. */}
      {onglet === 'efficacite' && (
        a.statut === 'verifiee' ? (
        <div style={{ background: 'var(--green-50)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--green-600)', fontWeight: 600 }}>
          <I n="check" s={14} style={{ display: 'inline-block', verticalAlign: '-2px' }} /> Efficacité vérifiée — {a.verifEfficacite}
        </div>
        ) : a.statut === 'realisee' && isRQ ? (
            <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 'var(--radius-md)', padding: 14, display: 'grid', gap: 8 }}>
              <label style={lbl} htmlFor="act-verif"><Help aide={AIDE_VERIF}>Vérification d’efficacité</Help> (obligatoire pour clôturer)</label>
              <textarea id="act-verif" value={verif} onChange={(e) => setVerif(e.target.value)} rows={2} placeholder="Ex. aucune récidive sur 30 jours…" style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
              <div style={{ width: 'fit-content' }}>
                <ConfirmButton
                  label="Vérifier l’efficacité et clôturer" icon="shield" variant="primary"
                  disabled={!verif.trim()}
                  titre={verif.trim() ? undefined : 'Renseignez d’abord la vérification d’efficacité'}
                  question={`Clôturer ${a.ref} ? L’événement sera signé et le statut ne pourra plus redescendre.`}
                  confirmer="Clôturer"
                  onConfirm={() => smq(`ged/actions/${a.documentId}/status`, { statut: 'verifiee', verifEfficacite: verif }, 'Efficacité vérifiée — événement signé.')} />
              </div>
            </div>
        ) : (
          <EmptyState titre="L’efficacité n’est pas encore vérifiable."
            indice={a.statut === 'realisee'
              ? 'L’action est réalisée : le RQ doit constater son efficacité avant clôture.'
              : 'La vérification s’ouvre une fois l’action marquée réalisée.'} />
        )
      )}

      {onglet === 'liens' && <Liens type="action" refCourante={a.ref} />}

      {/* PLUS D'ONGLET « JOURNAL » (RQ, 24/08/2026) — le bouton dédié, présent
         sur tous les registres, ouvre le même journal en fenêtre. Deux chemins
         pour la même chose obligeaient à savoir lequel prendre, et à les tenir
         tous les deux. Le journal n'est pas une rubrique du dossier : c'est sa
         preuve, elle s'ouvre par-dessus. */}

      </PanneauDossier>

      {/* Les transitions restent au pied du dossier quel que soit l'onglet
          consulté. Le bouton n'apparaît pas là où sa cible est déjà à l'écran. */}
      {canManage && geste && !(a.statut === 'realisee' && isRQ && onglet === 'efficacite') && (
        <BarreAction>
          {/* LE GESTE SUIVANT, ET CE QU'IL AFFIRME. « Démarrer » et « marquer
              réalisée » se ressemblaient : la phrase dit lequel engage quoi,
              à côté du bouton plutôt que dans une infobulle. */}
          <span style={{ flex: '1 1 240px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {gestePourMoi ? geste.explique : 'En attente de vérification d’efficacité par le RQ — il constate l’effet, puis clôture.'}
          </span>
          {/* Le rattrapage n'est offert que sur une action JAMAIS DÉMARRÉE :
              du travail engagé ne se déclare pas « erreur de saisie ». Le
              serveur applique la même borne. */}
          {isRQ && a.statut === 'ouverte' && <AnnulerAction a={a} />}
          {gestePourMoi && (geste.statut === 'verifiee'
            /* La clôture se SIGNE avec son texte de vérification : le bouton
               mène à l'onglet qui le porte, il ne transitionne pas d'ici. */
            ? <Bouton variante="primaire" taille="sm" onClick={() => setOnglet('efficacite')}>{geste.libelle} →</Bouton>
            : (
              <Bouton variante="primaire" taille="sm" disabled={estOccupe()}
                onClick={() => void transition(geste.statut, TOAST_GESTE[geste.statut] || 'Action mise à jour.')}>
                {estOccupe(geste.statut) ? 'En cours…' : geste.libelle}
              </Bouton>
            ))}
        </BarreAction>
      )}
    </div>
  );
}

/* L8 (11/10/2026) : le tri de la liste est celui du socle (`useTri`, même moteur
   que `<Tableau>`). L'ORDRE MÉTIER — en retard d'abord, puis l'échéance la plus
   proche — reste l'ordre par défaut ; les boutons permettent d'en sortir, et le
   troisième clic y revient. Les valeurs sont BRUTES (ISO, codes). */
const VALEURS_TRI_ACTIONS: Record<string, (a: GedAction) => string> = {
  ref: (a) => a.ref,
  echeance: (a) => a.echeance || '',
  statut: (a) => a.statut,
};

export function Actions() {
  const { actions, selectedAction, select, session, settings, loading, booted, smqFiltre } = useGed();
  const [showNew, setShowNew] = useState(false);
  const [brouillon, setBrouillon] = useState(false);
  const [q, setQ] = useState('');
  // Filtre demandé par le compteur qui nous a amenés ici — lu au montage
  // seulement (`go` l'efface, donc jamais de filtre hérité d'une visite passée).
  const [filtre, setFiltre] = useState(smqFiltre ?? 'toutes');
  const isRQ = session.roles.includes('rq');
  const sel = actions.find((a) => a.documentId === selectedAction) || null;
  const enChargement = !booted || (loading && actions.length === 0);

  const ouvertes = actions.filter(actionOuverte);
  const retard = actions.filter(isLate);
  const verifiees = actions.filter((a) => a.statut === 'verifiee');
  const miennes = actions.filter((a) => actionMienne(a, session.key));

  // Les tuiles désignent exactement le sous-ensemble qu'on veut consulter :
  // elles appliquent désormais le filtre au lieu d'être décoratives.
  const kpis = [
    { v: actions.length, l: 'Total', k: 'toutes' },
    /* « Ouvertes », comme la puce de filtre qu'elle actionne : la tuile était
       intitulée « En cours », qui est AUSSI un état de l'action — elle comptait
       donc, sous ce nom, les actions ouvertes ET en cours. */
    { v: ouvertes.length, l: 'Ouvertes', k: 'ouvertes' },
    { v: retard.length, l: 'En retard', k: 'retard', rouge: true },
    { v: verifiees.length, l: 'Efficacité vérifiée', k: 'verifiees' },
  ];

  const listeFiltree = useMemo(() => {
    const base = filtre === 'ouvertes' ? ouvertes
      : filtre === 'retard' ? retard
        : filtre === 'verifiees' ? verifiees
          : filtre === 'miennes' ? miennes
            : actions;
    const qq = q.trim().toLowerCase();
    const cherche = qq ? base.filter((a) => `${a.ref} ${a.titre} ${a.origineRef} ${userName(settings, a.responsable)}`.toLowerCase().includes(qq)) : base;
    // Le plus urgent d'abord : retards, puis échéance la plus proche.
    return [...cherche].sort((x, y) => {
      const lx = isLate(x) ? 0 : 1, ly = isLate(y) ? 0 : 1;
      if (lx !== ly) return lx - ly;
      if (!x.echeance) return 1;
      if (!y.echeance) return -1;
      return x.echeance.localeCompare(y.echeance);
    });
  }, [actions, filtre, q, settings, ouvertes, retard, verifiees, miennes]);
  const { tri, basculer: onTri, trier } = useTri<GedAction>(null, VALEURS_TRI_ACTIONS);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>  {/* `minmax(0, 1fr)` : un élément de grille refuse par défaut de
      descendre sous sa largeur de contenu, si bien qu'un registre large
      étirait le bandeau de titre et la barre de filtres hors du cadre
      (constaté sur les NC le 19/08/2026). Le `0` autorise la colonne à
      se réduire ; ce qui est large défile dans son propre conteneur. */}
      <EnteteEcran rubrique="Amélioration continue" clauseISO="8.7"
        titre={<>Plan d’actions <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-muted)' }}>(<Help aide={AIDE_CAPA}>CAPA</Help>)</span></>}
        actions={isRQ && (
          <BoutonCreer onClick={() => { setShowNew(true); select('selectedAction', null); }}>
            Nouvelle action{!showNew && brouillon ? ' (brouillon conservé)' : ''}
          </BoutonCreer>
        )} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12 }}>
        {enChargement ? <SkeletonTiles n={4} /> : kpis.map((k) => {
          const on = filtre === k.k;
          return (
            <button key={k.k} onClick={() => setFiltre(k.k)} className="ged-btn" aria-pressed={on}
              title={`Filtrer sur « ${k.l} »`}
              style={{ ...card, padding: '14px 16px', textAlign: 'start', cursor: 'pointer', font: 'inherit', fontFamily: 'var(--font-sans)', borderColor: on ? 'var(--cyan-200)' : undefined, background: on ? 'var(--cyan-50)' : 'var(--surface-card)' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 24, fontWeight: 600, color: k.rouge && k.v > 0 ? 'var(--red-600)' : 'var(--navy-900)', lineHeight: 1 }}>{k.v}</div>
              <div style={{ fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 6 }}>{k.l}</div>
            </button>
          );
        })}
      </div>

      {isRQ && <NewForm visible={showNew} onDone={() => setShowNew(false)} onDirty={setBrouillon} />}

      <div className="ged-2col-wide ged-colonnes-defilantes" style={{ display: 'grid', gridTemplateColumns: '380px minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
        <div>
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher une action…"
            chips={[
              { k: 'toutes', label: 'Toutes', n: actions.length },
              { k: 'ouvertes', label: 'Ouvertes', n: ouvertes.length },
              { k: 'retard', label: 'En retard', n: retard.length },
              { k: 'verifiees', label: 'Vérifiées', n: verifiees.length },
              { k: 'miennes', label: 'Les miennes', n: miennes.length },
            ]}
            actif={filtre} onChip={setFiltre}
            total={actions.length} affiches={listeFiltree.length}
            onReset={() => { setQ(''); setFiltre('toutes'); }} />
          <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <MentionTri>en retard d’abord, puis par échéance — la plus proche en tête.</MentionTri>
            <span style={{ flex: 1 }} />
            <BoutonsTri label="Trier le plan d’actions" tri={tri} onTri={onTri}
              options={[{ cle: 'ref', libelle: 'Référence' }, { cle: 'echeance', libelle: 'Échéance' }, { cle: 'statut', libelle: 'Statut' }]} />
          </div>
          <div style={{ ...card, overflow: 'hidden' }}>
            {enChargement ? <Skeleton rows={5} height={64} /> : trier(listeFiltree).map((a) => {
              const m = vocabMeta(ACTION_STATES, a.statut);
              const active = selectedAction === a.documentId;
              return (
                <LigneLien key={a.documentId} href={cheminDe('actions', a.ref)}
                  title={`Ouvrir ${a.ref}`}
                  onOuvrir={() => { select('selectedAction', a.documentId); setShowNew(false); }}
                  style={{ width: '100%', textAlign: 'start', background: active ? 'var(--cyan-50)' : 'none', borderTop: '1px solid var(--border-subtle)', padding: '12px 14px', fontFamily: 'var(--font-sans)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.ref}</span>
                    <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: m.bg, color: m.fg, fontSize: 11, fontWeight: 700 }}>{m.label}</span>
                    {isLate(a) && <LateBadge jours={joursRestants(a.echeance)} />}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', marginTop: 5, lineHeight: 1.35 }}>{a.titre}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                    Échéance {a.echeance ? fmtDate(a.echeance) : '— aucune'} · {a.origineRef || ACTION_ORIGINS.find((o) => o.key === a.origine)?.label}
                  </div>
                </LigneLien>
              );
            })}
            {!enChargement && listeFiltree.length === 0 && (
              <EmptyState
                titre={actions.length === 0 ? 'Aucune action au plan.' : 'Aucune action ne correspond à ces critères.'}
                indice={actions.length === 0
                  ? 'Les actions correctives peuvent aussi naître d’une non-conformité ou d’un constat d’audit.'
                  : 'Élargissez la recherche ou revenez à « Toutes ».'}
                action={actions.length > 0
                  ? <Bouton taille="sm" onClick={() => { setQ(''); setFiltre('toutes'); }}>Réinitialiser les filtres</Bouton>
                  : undefined} />
            )}
          </div>
        </div>
        {/* `key` : changer d'action remonte la fiche — onglet et saisie de
                vérification repartent de zéro. */}
        {sel ? <Detail key={sel.documentId} a={sel} /> : (
          <div style={{ ...card }}>
            <EmptyState titre="Fiche action" indice="Sélectionnez une action dans la liste pour la piloter." />
          </div>
        )}
      </div>
    </div>
  );
}
